import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useInviteVerification() {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [verifiedInvitation, setVerifiedInvitation] = useState<any>(null);

  const verifyCode = async (token: string, code: string): Promise<{ success: boolean; invitation?: any }> => {
    setIsVerifying(true);
    try {
      // Get invitation
      const { data: invitation, error: fetchError } = await supabase
        .from("user_invitation")
        .select("*")
        .eq("token", token)
        .eq("status", "pending")
        .single();

      if (fetchError || !invitation) {
        toast({
          title: "Invalid invitation",
          description: "This invitation link is invalid or has expired.",
          variant: "destructive",
        });
        return { success: false };
      }

      // Check expiration
      if (new Date(invitation.expires_at) < new Date()) {
        toast({
          title: "Invitation expired",
          description: "This invitation has expired. Please request a new one.",
          variant: "destructive",
        });
        return { success: false };
      }

      // Check attempts
      if (invitation.verification_attempts >= 5) {
        toast({
          title: "Too many attempts",
          description: "This invitation has been locked due to too many failed attempts.",
          variant: "destructive",
        });
        return { success: false };
      }

      // Verify code
      if (invitation.verification_code !== code) {
        // Increment attempts
        await supabase
          .from("user_invitation")
          .update({ verification_attempts: invitation.verification_attempts + 1 })
          .eq("id", invitation.id);

        toast({
          title: "Invalid code",
          description: `Incorrect verification code. ${4 - invitation.verification_attempts} attempts remaining.`,
          variant: "destructive",
        });
        return { success: false };
      }

      setVerifiedInvitation(invitation);
      return { success: true, invitation };
    } catch (error: any) {
      toast({
        title: "Verification error",
        description: error.message,
        variant: "destructive",
      });
      return { success: false };
    } finally {
      setIsVerifying(false);
    }
  };

  const createAccount = async (password: string, invitation: any, displayName: string) => {
    if (!invitation) {
      toast({
        title: "Error",
        description: "No verified invitation found.",
        variant: "destructive",
      });
      return false;
    }

    setIsCreatingAccount(true);
    try {
      // Call server-side edge function to handle account creation
      const { data, error } = await supabase.functions.invoke("accept-invitation", {
        body: {
          token: invitation.token,
          verificationCode: invitation.verification_code,
          password: password,
          displayName: displayName,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      // Auto-login with the password they just created
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password: password,
      });

      if (signInError) {
        console.error("Auto-login failed:", signInError);
        toast({
          title: "Account created!",
          description: "Please sign in with your new credentials.",
        });
      } else {
        toast({
          title: "Account created!",
          description: "Welcome to GravityAssist. Redirecting...",
        });
      }

      return true;
    } catch (error: any) {
      console.error("Error creating account:", error);
      toast({
        title: "Account creation failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsCreatingAccount(false);
    }
  };

  return {
    verifyCode,
    createAccount,
    isVerifying,
    isCreatingAccount,
    verifiedInvitation,
  };
}
